import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { ContractItem } from '@core/models/contract-item.model';
import { ContractSeller, ContractSellerItem, JsonContractSellerItem } from '@core/models/contract-seller.model';
import { ContractState, JsonContractState } from '@core/models/contract-state.model';
import { Contract } from '@core/models/contract.model';
import { BaseService } from '@core/services/base/base.service';
import { ErrorHandlerService, HttpError } from '@core/services/error-handler.service';
import { environment } from '@env/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

export interface ContractFilter {
  year: string;
  codUt?: string;
  nomUt?: string;
  searchElementById?: string;
  searchElementByName?: string;
  itemsPerPage?: number;
  page?: number;
}

export interface ContractStateFilter {
  year: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContractService extends BaseService {
  private contractItemRoute = '/contract_items';

  constructor(
    private snackBar: MatSnackBar,
    private router: Router,
    private httpService: HttpClient,
    private errorHandlerService: ErrorHandlerService
  ) {
    super();
  }

  public getContractList(filter): Observable<Contract[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récuperer la liste des contrats. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contracts?' + this.getParamsFromFilter(filter);

    return new Observable(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            let contracts: Contract[] = [];
            // if (response && response['data'] && Array.isArray(response['data'])) {
            //   contracts = response['data'].map(contract => new Contract(contract));
            // }
            if (response && Array.isArray(response)) {
              contracts = response.map(contract => new Contract(contract));
            }
            return contracts;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  // public getContract(id): Observable<Contract> {
  //   const httpErrors: HttpError[] = [
  //     {
  //       statusCode: 404,
  //       message: 'Impossible de récuperer le contrat. Veuillez essayer plus tard.',
  //     },
  //     {
  //       statusCode: 500,
  //       message: 'Une erreur est survenue. Veuillez essayer plus tard.',
  //     },
  //   ];
  //
  //   let url = environment.contract_microservice_api_base_url + '/contract/' + id;
  //
  //   return new Observable<Contract>(observer => {
  //     this.httpService
  //       .get(url)
  //       .pipe(
  //         map(response => {
  //           if (response && response['statusCode'] === 200) {
  //             return new Contract(response['data'] as Contract);
  //           } else {
  //             throw new Error('Erreur lors de la récupération du contrat');
  //           }
  //         })
  //       )
  //       .subscribe(
  //         (response: any) => observer.next(response),
  //         error => {
  //           this.errorHandlerService.showErrorMessage(httpErrors, error.status);
  //           observer.error(error);
  //         }
  //       );
  //   });
  // }

  public getContractStateList(filter): Observable<ContractState[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récuperer la liste des états du contrat. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_states?' + this.getParamsFromFilter(filter);

    return new Observable(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            let contractStates: ContractState[] = [];
            if (response && Array.isArray(response)) {
              contractStates = response.map(contract => new ContractState(contract as JsonContractState));
            }
            return contractStates;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public updateContractItem(contractItem: ContractItem): Observable<ContractItem> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: "Impossible de mettre à jour l'item contrat. Veuillez essayer plus tard.",
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_items/' + contractItem.id;

    return new Observable<ContractItem>(observer => {
      this.httpService.put(url, contractItem).subscribe(
        (response: any) => {
          observer.next(response);
          observer.complete(); // ✅ très important pour forkJoin!
        },
        error => {
          this.errorHandlerService.showErrorMessage(httpErrors, error.status);
          observer.error(error);
          observer.complete(); // on peut ou non compléter après une erreur
        }
      );
    });
  }

  public getContractItem(filter): Observable<ContractItem> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: "Impossible de récuperer l'item contrat. Veuillez essayer plus tard.",
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract_items/' + filter.year + '/' + filter.searchElementById;

    return new Observable<ContractItem>(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            if (response && response['statusCode'] === 200) {
              return new ContractItem(response['data']);
            }

            return null;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
          }
        );
    });
  }

  public getContractSellerToTransfer(year: string, ut: string): Observable<ContractSeller[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récuperer la liste des contrats. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract/seller/transfer/' + 'year/' + year + '/ut/' + ut;

    return new Observable(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            let contractSellers: ContractSeller[] = [];
            if (response && Array.isArray(response['data'] ?? null)) {
              contractSellers = response['data'].map(contractSeller => new ContractSeller(contractSeller));
            }
            return contractSellers;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public getContractSellerListByYear(year: string, ut: string): Observable<ContractSellerItem[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récuperer la liste des contrats. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract/seller/' + 'year/' + year + '/ut/' + ut;

    return new Observable(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            let ContractSellerItems: JsonContractSellerItem[] = [];
            if (response && Array.isArray(response['data'] ?? null)) {
              ContractSellerItems = response['data'].map(contractSellerItem => new ContractSellerItem(contractSellerItem));
            }
            return ContractSellerItems;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public getSellerByYear(year: string): Observable<ContractSeller[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récuperer la liste des contrats. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url = environment.contract_microservice_api_base_url + '/contract/seller/year/' + year;

    return new Observable(observer => {
      this.httpService
        .get(url)
        .pipe(
          map(response => {
            let contractSellers: ContractSeller[] = [];
            if (response && Array.isArray(response['data'] ?? null)) {
              contractSellers = response['data'].map(contractSeller => new ContractSeller(contractSeller));
            }
            return contractSellers;
          })
        )
        .subscribe(
          (response: any) => observer.next(response),
          error => {
            this.errorHandlerService.showErrorMessage(httpErrors, error.status);
            observer.error(error);
          }
        );
    });
  }

  public transferContractsToSeller(
    year: string,
    ut: string,
    sellerToTransfer: string,
    contracts: ContractSellerItem[]
  ): Observable<ContractSeller[]> {
    const httpErrors: HttpError[] = [
      {
        statusCode: 404,
        message: 'Impossible de récuperer la liste des contrats. Veuillez essayer plus tard.',
      },
      {
        statusCode: 500,
        message: 'Une erreur est survenue. Veuillez essayer plus tard.',
      },
    ];

    let url =
      environment.contract_microservice_api_base_url +
      '/contract/seller/transfer/' +
      'year/' +
      year +
      '/ut/' +
      ut +
      '/to/' +
      sellerToTransfer;

    const transferContracts = contracts.map(contract => contract.toTransferJson());

    return new Observable(observer => {
      this.httpService.post(url, transferContracts).subscribe(
        (response: any) => {
          return observer.next(response);
        },
        error => {
          this.errorHandlerService.showErrorMessage(httpErrors, error.status);
          observer.error(error);
        }
      );
    });
  }

  public create(contractItemData: any): Observable<any> {
    const url = environment.contract_microservice_api_base_url + this.contractItemRoute;

    return new Observable(observer => {
      this.httpService.post(url, contractItemData).subscribe(
        response => {
          if (response && response['data'] && response['data']['contractItem']) {
            observer.next(new ContractItem(response['data']['contractItem']));
          } else {
            observer.next(response);
          }
        },
        error => observer.error(this.catchError(error))
      );
    });
  }

  private catchError(error: any, routeRedirect: string = null): any {
    this.snackBar.open(error.error.data?.msg ? error.error.data.msg : 'Une erreur est survenue', null, {
      duration: 2000,
      verticalPosition: 'top',
    });

    if (routeRedirect) {
      this.router.navigate([routeRedirect]);
    }

    return error;
  }
}
